"use client"

import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "./Icon"
import { formatMoney } from "../utils/format"

const MODULE_TOP_UP_PAYBILL = "4123905"
// Modules whose page has its own All Transactions history.
const MODULES_WITH_ALL_TRANSACTIONS = ["FRP", "Groups", "UTBills"]

function RoleMenu({
  modules,
  assignedModules = [],
  user,
  payScoreSummary = null,
  payScoreLoading = false,
  onTransactionsClick,
  onTopUpClick,
  onUtBillsSetupClick,
  onUtBillsTriggerPayClick,
  utBillsTriggerPayLoading = false,
  onFrpAutoDisburseToggle,
  frpAutoDisburseSaving = false,
  tenantBalanceDue,
  groupBalanceDue,
  frpBalanceDue = 0,
  groupOutstanding = 0,
  utBillsOutstanding = 0,
  showFrpTopUpButton = false,
  utBillsBalanceDue = 0,
  showUtBillsTopUpButton = false,
  showUtBillsOpen = true,
  userGroupCount = null,
  onJoinGroupClick,
}) {
  const navigate = useNavigate()
  const [loadingModule, setLoadingModule] = useState(null)
  // Modules render as a vertical stack of collapsed rows (icon + name + open +
  // chevron); expanding a row reveals the full card (balance, statements, etc.).
  const [expandedModules, setExpandedModules] = useState({})
  // Per-module card refs + a "just expanded" target so we can scroll the newly
  // opened card into view (expanding the bottom row otherwise stays off-screen).
  const cardRefs = useRef({})
  const [scrollToModule, setScrollToModule] = useState(null)
  const toggleExpanded = (module) => {
    const willExpand = !expandedModules[module]
    setExpandedModules((prev) => ({ ...prev, [module]: !prev[module] }))
    if (willExpand) setScrollToModule(module)
  }

  useEffect(() => {
    if (!scrollToModule) return
    // Runs after the expanded body has been committed to the DOM.
    const el = cardRefs.current[scrollToModule]
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
    setScrollToModule(null)
  }, [scrollToModule])

  if (!modules || modules.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center bg-gray-50">
        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="gear" size="1.5rem" className="text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium mb-1 text-sm">No modules assigned</p>
        <p className="text-xs text-gray-500">Contact your administrator for module access</p>
      </div>
    )
  }

  const orderedModules = Array.from(new Set(Array.isArray(modules) ? modules : []))
    .sort((a, b) => {
        const rank = (m) => (m === "FRP" ? 0 : m === "UTBills" ? 1 : m === "Vehicles" ? 2 : m === "Groups" ? 99 : 3)
        return rank(a) - rank(b)
      })
  const assignedModuleSet = new Set(Array.isArray(assignedModules) ? assignedModules : [])
  const isWalletModule = (module) => module === "FRP" || module === "Groups" || module === "UTBills"
  const isModuleAssigned = (module) => assignedModuleSet.has(module)
  const hasFrpDisburseRole =
    user?.role === "M" ||
    user?.role === "L" ||
    (Array.isArray(user?.propertyRoles) && user.propertyRoles.some((pr) => pr?.role === "M" || pr?.role === "L"))
  const hasUtBillsTriggerRole =
    user?.role === "M" ||
    user?.role === "A" ||
    (Array.isArray(user?.propertyRoles) && user.propertyRoles.some((pr) => pr?.role === "M" || pr?.role === "A"))

  const resolveScoreBadgeClass = (scoreText, status) => {
    const score = parseFloat(scoreText)
    const normalizedStatus = String(status || "").toLowerCase()

    if (Number.isFinite(score) && score > 0) {
      if (score >= 90) return "bg-emerald-100 text-emerald-700 border-emerald-200"
      if (score >= 80) return "bg-green-100 text-green-700 border-green-200"
      if (score >= 70) return "bg-amber-100 text-amber-700 border-amber-200"
      if (score >= 50) return "bg-orange-100 text-orange-700 border-orange-200"
      return "bg-red-100 text-red-700 border-red-200"
    }

    if (normalizedStatus === "excellent") return "bg-emerald-100 text-emerald-700 border-emerald-200"
    if (normalizedStatus === "good") return "bg-green-100 text-green-700 border-green-200"
    if (normalizedStatus === "fair") return "bg-amber-100 text-amber-700 border-amber-200"
    if (normalizedStatus === "risk") return "bg-orange-100 text-orange-700 border-orange-200"
    if (normalizedStatus === "restricted") return "bg-red-100 text-red-700 border-red-200"
    return "bg-gray-100 text-gray-700 border-gray-200"
  }

  const renderModulePayScore = (module) => {
    const scoreNode =
      module === "FRP"
        ? payScoreSummary?.modules?.frp
        : module === "Groups"
          ? payScoreSummary?.modules?.group
          : null

    if (module !== "FRP" && module !== "Groups") return null

    const label = module === "FRP" ? "Rent PaySCORE" : "Groups PaySCORE"
    const rawScore = Number(scoreNode?.score)
    if (!(Number.isFinite(rawScore) && rawScore > 0)) return null
    const scoreText = `${rawScore.toFixed(1)}%`
    const status = scoreNode?.status || "N/A"

    return (
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">PayScore</span>
        <div
          className={`inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold leading-none whitespace-nowrap ${resolveScoreBadgeClass(rawScore, status)}`}
          title={`${label}: ${scoreText} (${status})`}
        >
          <Icon name="star-fill" size="0.5rem" className="shrink-0" />
          <span>{scoreText}</span>
        </div>
      </div>
    )
  }

  const getModuleInfo = (module) => {
    const moduleData = {
      FRP: {
        shortName: "Rentals",
        walletTitle: "Rentals",
        tag: "My Rental Module",
        label: "Wallet Balance",
        actionLabel: "Transactions",
        headerIcon: <Icon name="house-fill" size="1rem" className="text-emerald-600" />,
        iconBg: "bg-emerald-100",
        tagTextClass: "text-emerald-600",
        balance: tenantBalanceDue,
        headerClassName: "bg-emerald-50/70",
        accentBadgeClassName: "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50",
        primaryActionClassName: "bg-emerald-600 text-white hover:bg-emerald-700",
        cardClassName: "border-emerald-200 bg-white",
        statementsButtonClassName: "border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200",
      },
      Groups: {
        shortName: "Groups",
        walletTitle: "Groups",
        tag: "My Groups Module",
        label: "Wallet Balance",
        actionLabel: "Transactions",
        headerIcon: <Icon name="people-fill" size="1rem" className="text-sky-600" />,
        iconBg: "bg-sky-100",
        tagTextClass: "text-sky-600",
        balance: groupBalanceDue,
        headerClassName: "bg-sky-50/70",
        accentBadgeClassName: "border-sky-200 bg-white text-sky-700 hover:bg-sky-50",
        primaryActionClassName: "bg-sky-500 text-white hover:bg-sky-600",
        cardClassName: "border-sky-200 bg-white",
        statementsButtonClassName: "border-sky-200 bg-sky-100 text-sky-800 hover:bg-sky-200",
      },
      Vehicles: {
        shortName: "Vehicles",
        walletTitle: "Vehicles",
        tag: "My Vehicles Module",
        label: "Outstanding",
        actionLabel: "Transactions",
        headerIcon: <Icon name="car-front-fill" size="1rem" className="text-slate-500" />,
        iconBg: "bg-slate-100",
        tagTextClass: "text-slate-500",
        balance: 0,
        headerClassName: "bg-slate-50",
        accentBadgeClassName: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
        primaryActionClassName: "bg-slate-600 text-white hover:bg-slate-700",
        cardClassName: "border-slate-200 bg-white",
        statementsButtonClassName: "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200",
      },
      UTBills: {
        shortName: "Bills & Invoices",
        walletTitle: "Bills & Invoices",
        tag: "My Billing Module",
        label: "Wallet Balance",
        actionLabel: "Statements",
        headerIcon: <Icon name="receipt" size="1rem" className="text-teal-600" />,
        iconBg: "bg-teal-100",
        tagTextClass: "text-teal-600",
        balance: utBillsBalanceDue,
        headerClassName: "bg-teal-50/70",
        accentBadgeClassName: "border-teal-200 bg-white text-teal-700 hover:bg-teal-50",
        primaryActionClassName: "bg-teal-600 text-white hover:bg-teal-700",
        cardClassName: "border-teal-200 bg-white",
        statementsButtonClassName: "border-teal-200 bg-teal-100 text-teal-800 hover:bg-teal-200",
      },
    }

    return (
      moduleData[module] || {
        shortName: module,
        walletTitle: module,
        tag: module,
        label: "Balance",
        actionLabel: "Transactions",
        headerIcon: <Icon name="grid-fill" size="1rem" className="text-slate-500" />,
        iconBg: "bg-slate-100",
        tagTextClass: "text-slate-500",
        balance: 0,
        headerClassName: "bg-slate-50",
        accentBadgeClassName: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
        primaryActionClassName: "bg-slate-600 text-white hover:bg-slate-700",
        cardClassName: "border-slate-200 bg-white",
        statementsButtonClassName: "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200",
      }
    )
  }

  const handleClick = async (module) => {
    setLoadingModule(module)
    await new Promise((resolve) => setTimeout(resolve, 500))

    switch (module) {
      case "FRP":
        navigate("/app/frp")
        break
      case "Groups":
        navigate("/app/groups")
        break
      case "UTBills":
        navigate("/app/ut-bills")
        break
      case "Vehicles":
        alert("Vehicle module coming soon")
        break
      default:
        alert(`Redirecting to ${module} Module...`)
    }

    setLoadingModule(null)
  }

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3">
      {orderedModules.map((module, idx) => {
        const moduleInfo = getModuleInfo(module)
        const isLoading = loadingModule === module
        // UT Bills is always openable (anyone can pay bills or start a billing
        // company), so it is never shown as a balance-only card.
        const walletOnlyCard =
          isWalletModule(module) && !isModuleAssigned(module) && module !== "UTBills"
        const modulePayScore = walletOnlyCard ? null : renderModulePayScore(module)
        const canTopUp =
          (module === "Groups" && isModuleAssigned(module)) ||
          (module === "FRP" && showFrpTopUpButton && isModuleAssigned(module)) ||
          (module === "UTBills" && showUtBillsTopUpButton && isModuleAssigned(module))
        const showFrpAutoDisburse =
          module === "FRP" && isModuleAssigned(module) && hasFrpDisburseRole
        const canEnableFrpAutoDisburse =
          showFrpAutoDisburse &&
          typeof user?.bankName === "string" &&
          user.bankName.trim().length > 0 &&
          typeof user?.bankAccNumber === "string" &&
          user.bankAccNumber.trim().length > 0
        const rawAccountReference = String(user?.idNumber || "").trim()
        const moduleAccountReference =
          module === "UTBills"
            ? (rawAccountReference ? `U${rawAccountReference}` : "U + Your ID number")
            : rawAccountReference || "Your ID number"
        const balanceText = formatMoney(moduleInfo.balance || 0).replace("KES ", "")
        // Open is hidden for UT Bills until the user runs a company or has bills
        // (company registration lives on the dashboard Register menu).
        const showModuleOpen = !walletOnlyCard && (module !== "UTBills" || showUtBillsOpen)
        // Rentals, Groups and Bills & Invoices have All Transactions inside the
        // module, so their card keeps Statements only when it cannot open it.
        const showStatements = !MODULES_WITH_ALL_TRANSACTIONS.includes(module) || !showModuleOpen
        const isExpanded = !!expandedModules[module]
        // Amount owed in this module (invoiced - paid), shown in the collapsed row.
        const moduleDue =
          module === "FRP"
            ? Number(frpBalanceDue || 0)
            : module === "Groups"
              ? Number(groupOutstanding || 0)
              : module === "UTBills"
                ? Number(utBillsOutstanding || 0)
                : 0
        const showDue = moduleDue > 0

        return (
          <div
            key={idx}
            ref={(el) => {
              cardRefs.current[module] = el
            }}
            className={`relative scroll-mt-20 overflow-hidden rounded-xl sm:rounded-2xl border shadow transition-all duration-200 ${moduleInfo.cardClassName}`}
          >
            {/* ── Collapsed header (always visible): icon + name + chevron ── */}
            <div className={`flex items-center gap-2 sm:gap-3 ${moduleInfo.headerClassName} px-3 py-2.5 sm:px-4 sm:py-3`}>
              <button
                type="button"
                onClick={() => toggleExpanded(module)}
                aria-expanded={isExpanded}
                className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5 text-left"
              >
                <div className={`flex shrink-0 h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg sm:rounded-xl ${moduleInfo.iconBg}`}>
                  {moduleInfo.headerIcon}
                </div>
                <div className="min-w-0">
                  <p className={`hidden sm:block text-[10px] font-semibold uppercase tracking-widest leading-none truncate ${moduleInfo.tagTextClass}`}>
                    {walletOnlyCard ? "Wallet" : moduleInfo.tag}
                  </p>
                  <p className="text-[14px] sm:text-[16px] font-bold text-slate-800 leading-tight truncate">
                    {walletOnlyCard ? moduleInfo.walletTitle : moduleInfo.shortName}
                  </p>
                </div>
              </button>

              {(isWalletModule(module) || showDue) && (
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  {isWalletModule(module) && (
                    <span
                      title={`${moduleInfo.walletTitle} wallet balance`}
                      className={`flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] sm:text-[11px] font-bold tabular-nums ${moduleInfo.iconBg} ${moduleInfo.tagTextClass}`}
                    >
                      <Icon name="wallet2" size="0.7rem" className="opacity-70" aria-hidden="true" />
                      {balanceText}
                    </span>
                  )}

                  {showDue && (
                    <span className="whitespace-nowrap rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] sm:text-[11px] font-bold text-red-600">
                      Due: {formatMoney(moduleDue).replace("KES ", "")}
                    </span>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => toggleExpanded(module)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse module" : "Expand module"}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white/60"
              >
                <Icon name={isExpanded ? "chevron-up" : "chevron-down"} size="0.9rem" />
              </button>
            </div>

            {/* ── Expanded body: full card detail ── */}
            {isExpanded && (
              <div className="flex flex-col gap-2 sm:gap-3 border-t border-slate-100 bg-white/50 p-2.5 sm:p-4">

                {/* Balance */}
                <div>
                  <p className="text-[9px] sm:text-[10px] font-medium uppercase tracking-widest text-slate-400">
                    {moduleInfo.label}
                  </p>
                  <p className="mt-0.5 text-[22px] sm:text-[28px] font-black leading-none tracking-tight text-slate-900 truncate">
                    {balanceText}
                  </p>
                </div>

                {/* Statements, for cards that cannot open their module's All
                    Transactions (see showStatements). */}
                {showStatements && (
                  <button
                    type="button"
                    onClick={() => onTransactionsClick?.(module)}
                    aria-label="Statements"
                    className={`flex w-full items-center justify-center gap-1.5 rounded-lg sm:rounded-xl border px-2.5 sm:px-3.5 py-2 sm:py-2.5 text-[11px] sm:text-[13px] font-medium transition-colors ${moduleInfo.statementsButtonClassName}`}
                  >
                    <Icon name="clock-history" size="0.8rem" className="shrink-0" />
                    <span className="whitespace-nowrap">Statements</span>
                    <Icon name="chevron-right" size="0.7rem" className="shrink-0 text-slate-400" />
                  </button>
                )}

                {showFrpAutoDisburse && (
                  <div className="overflow-hidden rounded-xl border border-emerald-200 bg-white">
                    <div className="flex items-center justify-between gap-2 sm:gap-3 px-2.5 py-2 sm:px-4 sm:py-3">
                      <p className="text-[9px] sm:text-[11px] font-bold uppercase tracking-normal sm:tracking-wide text-slate-500 sm:text-slate-900 leading-tight">Auto<br />Disburse</p>
                      <button
                        type="button"
                        onClick={() => onFrpAutoDisburseToggle?.(!user?.autoDisburse)}
                        disabled={frpAutoDisburseSaving || (!canEnableFrpAutoDisburse && !user?.autoDisburse)}
                        aria-pressed={!!user?.autoDisburse}
                        className={`shrink-0 btn-toggle ${
                          user?.autoDisburse ? "active" : ""
                        } ${(frpAutoDisburseSaving || (!canEnableFrpAutoDisburse && !user?.autoDisburse)) ? "cursor-not-allowed opacity-50" : ""}`}
                        title={
                          frpAutoDisburseSaving
                            ? "Saving auto disburse setting"
                            : !canEnableFrpAutoDisburse && !user?.autoDisburse
                              ? "Add bank details in Profile to enable auto disburse"
                              : "Toggle auto disburse"
                        }
                      >
                        <div className="handle" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Paybill / Account strip */}
                <div className="relative rounded-lg sm:rounded-xl border border-slate-100 bg-slate-50 px-2 py-2 sm:px-3 sm:py-2.5">
                  {canTopUp && (
                    <button
                      type="button"
                      onClick={() => onTopUpClick?.(module)}
                      title="Top Up"
                      className={`absolute -right-1.5 -top-1.5 flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full border shadow-sm transition-colors ${moduleInfo.accentBadgeClassName}`}
                    >
                      <Icon name="plus-lg" size="0.6rem" />
                    </button>
                  )}
                  <div className="flex items-center justify-between gap-1 py-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 shrink-0">Paybill</span>
                    <span className="text-[10px] sm:text-[12px] font-bold text-slate-700 truncate text-right">{MODULE_TOP_UP_PAYBILL}</span>
                  </div>
                  <div className="my-1 border-t border-slate-100" />
                  <div className="flex items-center justify-between gap-1 py-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 shrink-0">Acc</span>
                    <span
                      className="min-w-0 flex-1 truncate text-right text-[10px] sm:text-[12px] font-bold text-slate-700 pl-1"
                      title={moduleAccountReference}
                    >
                      {moduleAccountReference}
                    </span>
                  </div>
                  {modulePayScore && (
                    <>
                      <div className="my-1 border-t border-slate-100" />
                      <div className="py-0.5">{modulePayScore}</div>
                    </>
                  )}
                </div>

                {/* Due badge */}
                {!walletOnlyCard && module === "FRP" && Number(frpBalanceDue || 0) > 0 && (
                  <span className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-[11px] font-semibold text-red-600 text-center">
                    Due: {formatMoney(frpBalanceDue).replace("KES ", "")}
                  </span>
                )}

                {/* Registration entry (UT Bills) */}
                {module === "UTBills" && (
                  <button
                    type="button"
                    onClick={() => onUtBillsSetupClick?.()}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-teal-300 bg-white py-2 text-[11px] font-bold text-teal-700 transition-colors hover:bg-teal-50 sm:text-xs"
                  >
                    <Icon name="gear" size="0.8rem" />
                    <span>Set up</span>
                  </button>
                )}

                {/* Manager trigger pay (UT Bills) */}
                {module === "UTBills" && hasUtBillsTriggerRole && (
                  <button
                    type="button"
                    onClick={() => onUtBillsTriggerPayClick?.()}
                    disabled={utBillsTriggerPayLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-teal-200 bg-teal-50 py-2 text-[11px] sm:text-xs font-bold text-teal-700 transition-colors hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {utBillsTriggerPayLoading && (
                      <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-teal-300 border-t-teal-700" />
                    )}
                    <span>{utBillsTriggerPayLoading ? "Processing..." : "Trigger UT Pay"}</span>
                  </button>
                )}

                {/* Join / Create CTAs — only on Groups card when user has no groups */}
                {module === "Groups" && isModuleAssigned(module) && userGroupCount === 0 && (
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => onJoinGroupClick?.()}
                      className="w-full rounded-lg border border-sky-200 bg-sky-50 py-2 text-[11px] sm:text-xs font-bold text-sky-700 hover:bg-sky-100 transition-colors"
                    >
                      Join a Group
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/app/groups", { state: { openCreate: true } })}
                      className="w-full rounded-lg border border-sky-500 bg-sky-500 py-2 text-[11px] sm:text-xs font-bold text-white hover:bg-sky-600 transition-colors"
                    >
                      Create a Group
                    </button>
                  </div>
                )}

                {/* Open — full-width. Hidden for UT Bills until the user runs a
                    company or has bills (company registration lives on the dashboard). */}
                {showModuleOpen && (
                  <button
                    type="button"
                    onClick={() => !isLoading && handleClick(module)}
                    className={`mt-auto w-full rounded-xl py-2.5 text-sm font-semibold tracking-wide transition-colors ${moduleInfo.primaryActionClassName}`}
                  >
                    Open
                  </button>
                )}
              </div>
            )}

            {isLoading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600"></div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default RoleMenu
